import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActivityOrigin, useActivitySourceStatus, useLibraryDetails } from 'libs.ecoa_shared';
import { ResourceType } from 'libs.library_manager';
import { AssetTypes, Localization } from 'libs.localization.common';
import { useLocalizeMessage } from 'libs.nucleus';
import { ButtonWithTooltip, ModalNav, MultiStepsModalWindow } from 'libs.react';
import { ImportStateProvider, useImportState } from './contexts/import_state.context';
import { useImportSteps, useImportSubmission, useStepStates } from './hooks';
const UnifiedImportActionContent = ({ activityId, assetId, isDisabled }) => {
    const { id: currentActivityId } = useParams(); // get activity id from url in case activity id is not provided
    const translate = useLocalizeMessage();
    const [documentType, setDocumentType] = useState(undefined);
    // TODO: Man! This is weird way of managing state... Can we refactor/simplify?
    // Note: hasInitialized* props are needed so that closing the modal and re-opening it works.
    const { cloneCheckStatus, setTargetLocalizationId, setSourceLocalizationId, isFilesLoading, resetState } = useImportState();
    const [hasInitializedSourceLocalizationId, setHasInitializedSourceLocalizationId] = useState(false);
    const [hasInitializedTargetLocalizationId, setHasInitializedTargetLocalizationId] = useState(false);
    const { data: currentActivityResponse } = useLibraryDetails(activityId || currentActivityId, ResourceType.ACTIVITY);
    const currentActivity = currentActivityResponse?.data.data.data;
    const { activityOrigin, sourceActivity, isSourceTranslationsAvailable } = useActivitySourceStatus(currentActivityId);
    const displayHeader = currentActivity?.short_name || '';
    // Constants
    const DEFAULT_ACTIVITY_VERSION = '1.0';
    // Utility function to create activity localization
    const createActivityLocalization = (activityId, version = DEFAULT_ACTIVITY_VERSION) => {
        return new Localization({
            type: AssetTypes.ACTIVITY,
            id: activityId,
            version,
        }).getId();
    };
    // Calculate localization IDs
    const { targetLocalizationId, sourceLocalizationId } = useMemo(() => ({
        targetLocalizationId: currentActivity ? createActivityLocalization(currentActivity.identifier) : '',
        sourceLocalizationId: sourceActivity?.identifier ? createActivityLocalization(sourceActivity.identifier) : '',
    }), [currentActivity, sourceActivity?.identifier]);
    // Set localization IDs in context when they change
    useEffect(() => {
        if (targetLocalizationId) {
            setTargetLocalizationId(targetLocalizationId);
            setHasInitializedTargetLocalizationId(true);
        }
    }, [targetLocalizationId, setTargetLocalizationId, hasInitializedTargetLocalizationId]);
    useEffect(() => {
        if (sourceLocalizationId) {
            setSourceLocalizationId(sourceLocalizationId);
            setHasInitializedSourceLocalizationId(true);
        }
    }, [sourceLocalizationId, setSourceLocalizationId, hasInitializedSourceLocalizationId]);
    const { isSubmitting, handleSubmit } = useImportSubmission({ documentType, assetId });
    const steps = useImportSteps({
        documentType,
        setDocumentType,
        assetId,
        origin: activityOrigin,
        activity: currentActivity,
        isSourceTranslationsAvailable,
    });
    const stepStates = useStepStates({ documentType, cloneCheckStatus: cloneCheckStatus ?? false });
    const onSubmit = () => {
        handleSubmit();
    };
    const handleClose = () => {
        // Reset state when modal is closed to prevent caching issues
        resetState();
        setHasInitializedSourceLocalizationId(false);
        setHasInitializedTargetLocalizationId(false);
        setDocumentType(undefined);
    };
    const isActionDisabled = isSubmitting || isDisabled || isFilesLoading || activityOrigin === ActivityOrigin.CROSS_WORKSPACE_CLONED;
    const tooltipText = activityOrigin === ActivityOrigin.CROSS_WORKSPACE_CLONED
        ? translate('Import translations is disabled for activities copied from another workspace. To make changes, please clone this activity.')
        : translate('Import translations is not available now');
    return (_jsx(ModalNav, { actionButton: _jsx(ButtonWithTooltip, { label: translate('Import translation'), disabled: isActionDisabled, tooltip: tooltipText, enableTooltip: isActionDisabled }), modalWindow: _jsx(MultiStepsModalWindow, { title: translate('Import translations'), subtitle: displayHeader, steps: steps, stepStates: stepStates, submitLabel: translate('Import'), showStepText: true }), onSubmit: onSubmit, onClose: handleClose }));
};
export const UnifiedImportAction = ({ activityId, assetId, isDisabled }) => {
    return (_jsx(ImportStateProvider, { children: _jsx(UnifiedImportActionContent, { activityId: activityId, assetId: assetId, isDisabled: isDisabled }) }));
};
